import { ApiError, api, type MediaItem } from "./api";

export async function uploadMedia(file: File, onProgress: (ratio: number) => void): Promise<MediaItem> {
  if (file.size < 1) throw new ApiError("Le fichier est vide", 400);
  const started = await api<{ mediaId: string; partSize: number; partCount: number }>("/api/media/uploads", {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type || "",
      size: file.size,
    }),
  });
  const parts: { partNumber: number; etag: string }[] = [];
  try {
    let offset = 0;
    let partNumber = 1;
    while (offset < file.size) {
      const end = Math.min(file.size, offset + started.partSize);
      const chunk = file.slice(offset, end);
      const etag = await sendPart(started.mediaId, partNumber, chunk, (loaded) => {
        onProgress((offset + loaded) / file.size);
      });
      parts.push({ partNumber, etag });
      offset = end;
      partNumber += 1;
    }
    const done = await api<{ media: MediaItem }>(`/api/media/uploads/${started.mediaId}/complete`, {
      method: "POST",
      body: JSON.stringify({ parts }),
    });
    onProgress(1);
    return done.media;
  } catch (error) {
    await api(`/api/media/uploads/${started.mediaId}`, { method: "DELETE" }).catch(() => undefined);
    throw error;
  }
}

function sendPart(mediaId: string, partNumber: number, chunk: Blob, onProgress: (loaded: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `/api/media/uploads/${mediaId}/parts/${partNumber}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("content-type", "application/octet-stream");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      let payload: { error?: string; etag?: string } = {};
      try {
        payload = JSON.parse(xhr.responseText) as { error?: string; etag?: string };
      } catch {
        payload = {};
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload.etag) {
        resolve(payload.etag);
        return;
      }
      reject(new ApiError(payload.error || "Envoi interrompu", xhr.status));
    };
    xhr.onerror = () => reject(new ApiError("Réseau indisponible pendant l'envoi", 0));
    xhr.send(chunk);
  });
}
